import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/user-management.css";
import "../styles/role-management.css";
import {
  getRoles,
  createRole,
  patchRole,
  deleteRole,
} from "../services/roleService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import FormModal from "../components/ui/FormModal";
import ConfirmModal from "../components/ui/ConfirmModal";
import { useAppContext } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";

const emptyForm = { name: "", description: "" };

const RoleManagementPage = () => {
  const { setActiveSection } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const didInit = useRef(false);
  const didCheckRole = useRef(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editError, setEditError] = useState("");

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createError, setCreateError] = useState("");

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    isBulk: false,
    count: 0,
  });

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  // Role access check
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (didCheckRole.current) return;
      didCheckRole.current = true;
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  // Load roles
  useEffect(() => {
    (async () => {
      if (didInit.current) return;
      didInit.current = true;
      try {
        setLoading(true);
        const data = await getRoles();
        setRoles(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load roles");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Debounce search
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Sync URL params
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const searchStr = String(searchInput || "").trim();
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");
    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) setSearchParams(params, { replace: true });
  }, [searchInput, searchParams, setSearchParams]);

  const filteredRoles = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        String(r.name || "")
          .toLowerCase()
          .includes(q) ||
        String(r.description || "")
          .toLowerCase()
          .includes(q),
    );
  }, [roles, debouncedSearch]);

  // Selection
  const allSelected = useMemo(
    () =>
      filteredRoles.length > 0 &&
      selectedIds.size === filteredRoles.length,
    [filteredRoles, selectedIds],
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRoles.map((r) => r.role_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Edit
  const startEdit = (role) => {
    setEditingId(role.role_id);
    setEditForm({ name: role.name || "", description: role.description || "" });
    setEditError("");
    setEditOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
    setEditError("");
    setEditOpen(false);
  };

  const saveEdit = async () => {
    if (editingId === null || editingId === undefined) return;
    const idNum = Number(editingId);
    if (!Number.isInteger(idNum)) {
      toast.error("Invalid role id");
      return;
    }
    const payload = {
      name: editForm.name.trim(),
      description: editForm.description.trim(),
    };
    if (!payload.name) {
      setEditError("Name is required");
      return;
    }
    try {
      const updated = await patchRole(idNum, payload);
      const merged =
        updated && typeof updated === "object" ? updated : payload;
      setRoles((prev) =>
        prev.map((r) =>
          Number(r.role_id) === idNum ? { ...r, ...merged } : r,
        ),
      );
      toast.success(`Role "${payload.name}" has been updated successfully!`);
      cancelEdit();
    } catch (e) {
      setEditError(e?.message || "Failed to update role");
      toast.error(e?.message || "Failed to update role");
    }
  };

  // Delete single
  const removeSingle = (id) => {
    const role = roles.find((r) => r.role_id === id);
    setConfirmDelete({
      open: true,
      id,
      name: role?.name || "Role",
      isBulk: false,
      count: 0,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id, name } = confirmDelete;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
    try {
      await deleteRole(id);
      setRoles((prev) => prev.filter((r) => r.role_id !== id));
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      toast.success(`Role "${name}" has been deleted successfully!`);
    } catch (e) {
      toast.error(e?.message || "Failed to delete role");
    }
  };

  // Bulk delete
  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count: selectedIds.size,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await deleteRole(id);
      }
      setRoles((prev) => prev.filter((r) => !selectedIds.has(r.role_id)));
      setSelectedIds(new Set());
      toast.success(`${ids.length} role${ids.length > 1 ? "s" : ""} deleted successfully!`);
    } catch (e) {
      toast.error(e?.message || "Failed to delete selected roles");
    }
  };

  // Create
  const openCreateRole = () => {
    setCreateForm(emptyForm);
    setCreateError("");
    setCreating(true);
  };

  const cancelCreateRole = () => {
    setCreating(false);
    setCreateForm(emptyForm);
    setCreateError("");
  };

  const submitCreate = async () => {
    const payload = {
      name: createForm.name.trim(),
      description: createForm.description.trim(),
    };
    if (!payload.name) {
      setCreateError("Name is required");
      return;
    }
    try {
      const created = await createRole(payload);
      const normalized =
        created?.role_id ? created
        : created?.id ? { ...created, role_id: created.id }
        : created;
      setRoles((prev) => [normalized, ...prev]);
      toast.success(`Role "${payload.name}" has been created successfully!`);
      cancelCreateRole();
    } catch (e) {
      setCreateError(e?.message || "Failed to create role");
      toast.error(e?.message || "Failed to create role");
    }
  };

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h2 className="page-title">Role Management</h2>
              <p className="page-subtitle">
                View, create, edit, and delete roles
              </p>
            </div>
            <div className="roles-toolbar">
              <div className="roles-search">
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search by name, description"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button
                  className="secondary-btn sm"
                  onClick={() => setSearchInput("")}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>
              <button className="primary-btn" onClick={openCreateRole}>
                + Create Role
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          {selectedIds.size > 0 && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "1rem 1.25rem",
                background: "#eff6ff",
                borderRadius: "0.75rem",
                border: "1px solid #bfdbfe",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                <span
                  style={{
                    fontWeight: "600",
                    color: "#1e40af",
                    fontSize: "0.9375rem",
                  }}
                >
                  {selectedIds.size}{" "}
                  {selectedIds.size === 1 ? "role" : "roles"} selected
                </span>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <button className="danger-btn sm" onClick={bulkDelete}>
                    Delete Selected
                  </button>
                  <button className="secondary-btn sm" onClick={clearSelection}>
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ?
            <div className="no-results">Loading roles...</div>
          : <div className="users-table-container">
              <div className="users-table">
                <div className="users-table-wrapper">
                  <div className="users-table-header roles-cols">
                    <div className="cell checkbox">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </div>
                    <div className="cell">Name</div>
                    <div className="cell">Description</div>
                    <div className="cell actions">Actions</div>
                  </div>
                  {filteredRoles.length === 0 ? null : (
                    filteredRoles.map((role) => (
                      <div
                        key={role.role_id}
                        className={`users-table-row roles-cols${
                          selectedIds.has(role.role_id) ? " selected" : ""
                        }`}
                      >
                        <div className="cell checkbox">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(role.role_id)}
                            onChange={() => toggleSelect(role.role_id)}
                          />
                        </div>
                        <div className="cell role-name-cell">{role.name}</div>
                        <div className="cell role-desc-cell">
                          {role.description || "-"}
                        </div>
                        <div className="cell actions">
                          <div
                            className="actions-menu-wrapper"
                            style={{
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            <button
                              className="action-menu-btn"
                              aria-haspopup="true"
                              aria-expanded={activeDropdown === role.role_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (activeDropdown === role.role_id) {
                                  setActiveDropdown(null);
                                } else {
                                  setDropdownDirection(
                                    getActionDropdownDirection(e.currentTarget),
                                  );
                                  setActiveDropdown(role.role_id);
                                }
                              }}
                            >
                              Actions ▼
                            </button>
                            {activeDropdown === role.role_id && (
                              <div
                                className={`action-dropdown-menu${
                                  dropdownDirection === "up" ?
                                    " dropdown-up"
                                  : ""
                                }`}
                                role="menu"
                              >
                                <button
                                  className="action-menu-item"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    startEdit(role);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="action-menu-item danger"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    removeSingle(role.role_id);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {filteredRoles.length === 0 && (
                <div className="no-results">No roles found.</div>
              )}
            </div>
          }

        </div>
      </div>

      {/* Edit Modal */}
      <FormModal
        open={editOpen}
        title="Edit Role"
        onCancel={cancelEdit}
        size="compact"
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={saveEdit}
            >
              Save
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </>
        }
      >
        {editError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {editError}
          </div>
        )}
        <div className="role-card create-card">
          <div className="field">
            <label>Name</label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="Role name"
            />
          </div>
          <div className="field">
            <label>Description</label>
            <input
              type="text"
              value={editForm.description}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="Short description"
            />
          </div>
        </div>
      </FormModal>

      {/* Create Modal */}
      <FormModal
        open={creating}
        title="Create Role"
        size="compact"
        onCancel={cancelCreateRole}
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={submitCreate}
            >
              Create
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={cancelCreateRole}
            >
              Cancel
            </button>
          </>
        }
      >
        {createError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {createError}
          </div>
        )}
        <div className="role-card create-card">
          <div className="field">
            <label>Name</label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="Role name"
              required
            />
          </div>
          <div className="field">
            <label>Description</label>
            <input
              type="text"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="Short description"
            />
          </div>
        </div>
      </FormModal>

      <ConfirmModal
        open={confirmDelete.open}
        title={confirmDelete.isBulk ? "Confirm Bulk Delete" : "Confirm Delete"}
        message={
          confirmDelete.isBulk ?
            `Are you sure you want to delete ${
              confirmDelete.count
            } ${
              confirmDelete.count === 1 ? "role" : "roles"
            }? This action cannot be undone.`
          : `Are you sure you want to delete the role "${
              confirmDelete.name
            }"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={
          confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
        }
        onCancel={() =>
          setConfirmDelete({
            open: false,
            id: null,
            name: "",
            isBulk: false,
            count: 0,
          })
        }
        isDanger={true}
      />
    </>
  );
};

export default RoleManagementPage;


